import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '@/features/snackbar';

import { gql } from '@/utilities/gql';
import { makeGqlMutation } from '@/utilities/gqlQuery';

import {
  PORTFOLIO_QUERY_KEY,
  PORTFOLIOS_QUERY_KEY,
} from '@/hooks/portfolio/usePortfolio';
import type { PortfolioQueryClientData } from '@/hooks/portfolio/usePortfolio';

import { PORTFOLIO_FRAGMENT } from '@/hooks/portfolio/usePortfolio';

type Args = {
  onSuccess: () => void;
  onError: (error: unknown) => void;
};

const MUTATION = gql(
  `
  mutation rematchHolding($id: String!, $uniqueSymbol: String!) {
        rematchHolding(id: $id, uniqueSymbol: $uniqueSymbol) {
          ...portfolioFields
        }
      }
`,
  [PORTFOLIO_FRAGMENT]
);

const mutation = makeGqlMutation(MUTATION);

export function useRematchHoldingMutation({ onSuccess, onError }: Args) {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return mutation({
    onSuccess: (data, { uniqueSymbol }) => {
      const portfolio = data.rematchHolding;
      addSnackbar({
        type: 'positive',
        message: `Added ${uniqueSymbol} to your portfolio`,
        lifeTime: 2000,
      });
      queryClient.setQueryData<PortfolioQueryClientData>(
        [PORTFOLIO_QUERY_KEY, portfolio.id],
        () => portfolio
      );
      onSuccess?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PORTFOLIOS_QUERY_KEY });
    },
    onError: (error: unknown, { uniqueSymbol }) => {
      onError?.(error);
      addSnackbar({
        type: 'negative',
        message: `Failed to add ${uniqueSymbol} to your portfolio`,
        lifeTime: 1500,
      });
    },
  });
}
